import { memo, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Switch, Label, Link } from '@fluentui/react-components'
import { LockClosedRegular, LockOpenRegular } from '@fluentui/react-icons'
import { projectHelper } from '../../common/src/helpers'
import { addRestrictedTag, removeRestrictedTag } from '../../common/src/actions/tagsAPI'
import { useToastController } from '@fluentui/react-components'
import { FluentToast } from '../toast/FluentToast'
import { useCanAccessRestrictedProjects } from '../../hooks/restrictedProjectsHooks'

// Helper function to extract clean error message from various formats
const extractErrorMessage = (error: any) => {
  if (Array.isArray(error)) {
    return error[0]
  }
  if (typeof error === 'string') {
    try {
      // Try to parse as JSON in case it's a stringified array
      const parsed = JSON.parse(error)
      if (Array.isArray(parsed)) {
        return parsed[0]
      }
      return parsed
    } catch {
      // If parsing fails, return the string as-is
      return error
    }
  }
  return error
}

interface RestrictedProjectToggleProps {
  project: any // Immutable project object
  readOnly?: boolean
  className?: string
}

export const RestrictedProjectToggle = memo<RestrictedProjectToggleProps>(({
  project,
  readOnly = false,
  className
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { dispatchToast } = useToastController()
  const canAccessRestrictedProjects = useCanAccessRestrictedProjects()

  const isRestricted = projectHelper.isRestricted(project)

  const handleDisabledToggleClick = useCallback(() => {
    if (!canAccessRestrictedProjects) {
      dispatchToast(
        <FluentToast>
          Restricted projects are available with Business plans.{' '}
          <Link href="https://appfluence.com/help/article/restricted-projects-enhanced-security/" target="_blank">
            Learn more
          </Link>
          {' | '}
          <Link href="/manage/upgrade/">
            Upgrade to Business
          </Link>
        </FluentToast>,
        { intent: 'info', timeout: 5000 }
      )
    }
  }, [canAccessRestrictedProjects, dispatchToast])

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (!canAccessRestrictedProjects && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault()
      handleDisabledToggleClick()
    }
  }, [canAccessRestrictedProjects, handleDisabledToggleClick])

  const handleToggleRestricted = useCallback(async (event: any, data: { checked: boolean }) => {
    if (readOnly || !canAccessRestrictedProjects) return

    const { checked } = data
    
    try {
      if (checked) {
        // Add restricted tag
        const action = addRestrictedTag({ object: project })
        const result = await dispatch(action)
        
        if (result.error) {
          // Try different paths for error message extraction
          let errorMessage = 'Failed to restrict project'
          
          // Check the response body for error message
          if (result.payload?.response?.error) {
            errorMessage = extractErrorMessage(result.payload.response.error)
          } else if (result.payload?.error) {
            errorMessage = extractErrorMessage(result.payload.error)
          } else if (result.payload?.message) {
            errorMessage = result.payload.message
          } else if (result.error?.message) {
            errorMessage = result.error.message
          }
          
          dispatchToast(
            <FluentToast>{errorMessage}</FluentToast>,
            { intent: 'error', timeout: 5000 }
          )
        } else {
          dispatchToast(
            <FluentToast>Project restricted successfully</FluentToast>,
            { intent: 'success', timeout: 3000 }
          )
        }
      } else {
        // Remove restricted tag
        const action = removeRestrictedTag({ object: project })
        const result = await dispatch(action)
        
        if (result.error) {
          // Try different paths for error message extraction
          let errorMessage = 'Failed to unrestrict project'
          
          // Check the response body for error message
          if (result.payload?.response?.error) {
            errorMessage = extractErrorMessage(result.payload.response.error)
          } else if (result.payload?.error) {
            errorMessage = extractErrorMessage(result.payload.error)
          } else if (result.payload?.message) {
            errorMessage = result.payload.message
          } else if (result.error?.message) {
            errorMessage = result.error.message
          }
          
          dispatchToast(
            <FluentToast>{errorMessage}</FluentToast>,
            { intent: 'error', timeout: 5000 }
          )
        } else {
          dispatchToast(
            <FluentToast>Project unrestricted successfully</FluentToast>,
            { intent: 'success', timeout: 3000 }
          )
        }
      }
    } catch (error) {
      dispatchToast(
        <FluentToast>An error occurred while updating project settings</FluentToast>,
        { intent: 'error', timeout: 5000 }
      )
    }
  }, [dispatch, project, readOnly, dispatchToast, canAccessRestrictedProjects])

  return (
    <div className={className}>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 flex-1">
          {isRestricted ? (
            <LockClosedRegular className="text-orange-500" />
          ) : (
            <LockOpenRegular className="text-gray-500" />
          )}
          <Label 
            htmlFor="restricted-toggle"
            className={!canAccessRestrictedProjects ? 'text-gray-500' : ''}
          >
            {t('project.menu.restrict')}
          </Label>
        </div>
        <div 
          onClick={!canAccessRestrictedProjects ? handleDisabledToggleClick : undefined}
          onKeyDown={!canAccessRestrictedProjects ? handleKeyDown : undefined}
          role={!canAccessRestrictedProjects ? 'button' : undefined}
          tabIndex={!canAccessRestrictedProjects ? 0 : undefined}
          aria-label={!canAccessRestrictedProjects ? 'Learn about restricted projects upgrade options' : undefined}
          style={{ cursor: !canAccessRestrictedProjects ? 'pointer' : 'default' }}
        >
          <Switch
            id="restricted-toggle"
            checked={isRestricted}
            onChange={handleToggleRestricted}
            disabled={readOnly || !canAccessRestrictedProjects}
          />
        </div>
      </div>
      <p className="text-sm text-gray-600 mt-2">
        {isRestricted 
          ? t('project.restricted_project_tooltip')
          : (
            <>
              Restrict project access to current members only.
              {!canAccessRestrictedProjects && (
                <>
                  {' Available with Business plans. '}
                  <Link href="https://appfluence.com/help/article/restricted-projects-enhanced-security/" target="_blank">
                    Learn more
                  </Link>
                  {' | '}
                  <Link href="/manage/upgrade/">
                    Upgrade to Business
                  </Link>
                </>
              )}
            </>
          )
        }
      </p>
    </div>
  )
})